/**
 * The Contacts view's sidebar (Google Contacts' left nav): Add contact, then All, Team, Clients and Email contacts —
 * each opens `/{ws}/contacts[?kind=]`. The Contacts screen hides its own filter and Add contact while this shows.
 */
import type { ContactKind } from "@bundle/contracts";
import { Shell, type MessageKey, type OpenTarget } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { Button, SidebarNavRow, Text, useFeatureAccess, useT, useWorkspace } from "@bundle/ui";
import { requestNewContact } from "../screens/mail/contactIntents.ts";

type Props = { activeUrl: string; open: (url: string, target?: OpenTarget) => void };

const ROWS: { kind: ContactKind | undefined; icon: IconName; label: MessageKey }[] = [
  { kind: undefined, icon: "contacts", label: "mail.contacts.filter.all" },
  { kind: "team", icon: "users", label: "mail.contacts.filter.team" },
  { kind: "client", icon: "user", label: "mail.contacts.filter.client" },
  { kind: "email", icon: "mail", label: "mail.contacts.filter.email" },
];

export function SidebarContacts({ activeUrl, open }: Props) {
  const t = useT();
  const ws = useWorkspace();
  const access = useFeatureAccess();
  const active = Shell.parseUrl(activeUrl);
  const current = active?.type === "contacts" ? active : undefined;
  const url = (kind: ContactKind | undefined) => Shell.formatUrl({ type: "contacts", ws: ws.slug, ...(kind === undefined ? {} : { kind }) });
  const add = () => {
    if (current === undefined) open(url(undefined));
    requestNewContact();
  };
  return (
    <>
      <div className="sidebar-view-header">
        <Text variant="headline" weight="semibold">
          {t("shell.view.contacts")}
        </Text>
      </div>
      {access("contacts") === "full" ? (
        <div className="sidebar-view-note">
          <Button variant="secondary" icon="plus" label={t("mail.contacts.add")} onPress={add} testID="sidebar-contacts-create" />
        </div>
      ) : null}
      <div className="sidebar-group" data-testid="sidebar-contacts-kinds">
        {ROWS.map((r) => (
          <SidebarNavRow
            key={r.kind ?? "all"}
            icon={r.icon}
            label={t(r.label)}
            selected={current !== undefined && current.kind === r.kind}
            onPress={() => open(url(r.kind))}
            testID={`sidebar-contacts-${r.kind ?? "all"}`}
          />
        ))}
      </div>
    </>
  );
}
